import { type AnyFormApi, useStore } from '@tanstack/react-form';
import { useEffect, useRef } from 'react';

import { useScrollIntoView } from './useScrollIntoView';

type FieldMetaLike = { errors?: unknown[] } | undefined;

type FormFocusManagerOptions = {
  autoFocus?: boolean;
};

const FOCUSABLE_SELECTOR =
  'input, select, textarea, button, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

const LABEL_SUFFIX = '-label';

function hasError(meta: FieldMetaLike): boolean {
  return (
    Array.isArray(meta?.errors) && meta.errors.some((error) => error != null)
  );
}

function isNativelyFocusable(element: Element): boolean {
  return element.matches(FOCUSABLE_SELECTOR);
}

const AUTOFOCUSABLE_CONTROL_SELECTOR =
  'input:not([type="hidden"]), select, textarea, [contenteditable="true"]';

/**
 * Whether an element is a value-entry control that should receive autofocus.
 *
 * Autofocus mirrors native `autofocus`: it belongs in a real input, never on an
 * action button or other incidental focusable (a drag handle, an
 * icon-with-tooltip, etc.). A field can resolve to such a non-input focus
 * target — e.g. a `Fieldset` whose value is synthetic (a question count)
 * wrapping a list with edit/delete actions and type-icon tooltips — and
 * stealing focus there pops a tooltip and is jarring, especially on a read-only
 * view. When the first owned field has no such control (display-only content),
 * autofocus is skipped; any nested form (e.g. an inline editor) still autofocus
 * its own first field via its own manager.
 */
function isAutofocusableControl(element: Element): boolean {
  return element.matches(AUTOFOCUSABLE_CONTROL_SELECTOR);
}

type FieldTarget = {
  /** The field id the target resolves to. */
  fieldId: string;
  /** Element to scroll into view (the input or its `FormField` wrapper). */
  scrollTarget: HTMLElement;
  /** Element to focus, when one is focusable. */
  focusTarget: HTMLElement | null;
};

/**
 * Resolves the DOM elements for a given field.
 *
 * Each field is rendered through `FormField`, which stamps the field id onto the
 * input via `id={inputId}`. Simple inputs (text, number) are themselves
 * focusable. Composite inputs like `Select` put the id on a wrapper `div`, so we
 * fall back to the first focusable descendant.
 */
function resolveFieldTarget(
  fieldId: string,
  doc: Document,
): Omit<FieldTarget, 'fieldId'> | null {
  const byId = doc.getElementById(fieldId);
  if (byId) {
    const focusTarget = isNativelyFocusable(byId)
      ? byId
      : byId.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    return { scrollTarget: byId, focusTarget };
  }

  // `Select` and other composites don't always expose the id on the focusable
  // node; fall back to the `FormField` wrapper, which is labelled by id.
  const wrapper = doc.querySelector<HTMLElement>(
    `[aria-labelledby="${CSS.escape(fieldId)}-label"]`,
  );
  if (wrapper) {
    return {
      scrollTarget: wrapper,
      focusTarget: wrapper.querySelector<HTMLElement>(FOCUSABLE_SELECTOR),
    };
  }

  return null;
}

/**
 * Reads the field ids that `FormField` rendered, in document (visual) order.
 *
 * `FormField` labels each field wrapper with `aria-labelledby="${id}-label"`, so
 * the rendered fields can be recovered from the DOM without the caller having to
 * maintain a parallel ordered list.
 */
function getRenderedFieldIds(doc: Document): string[] {
  return Array.from(doc.querySelectorAll<HTMLElement>('[aria-labelledby]'))
    .map((el) => el.getAttribute('aria-labelledby') ?? '')
    .filter((value) => value.endsWith(LABEL_SUFFIX))
    .map((value) => value.slice(0, -LABEL_SUFFIX.length));
}

function resolveField(
  fieldId: string | undefined,
  doc: Document,
): FieldTarget | null {
  if (!fieldId) {
    return null;
  }
  const target = resolveFieldTarget(fieldId, doc);
  return target ? { fieldId, ...target } : null;
}

/**
 * Finds the first rendered field (in document order) whose `fieldMeta` reports a
 * validation error and resolves its DOM elements.
 *
 * Field order is derived from the DOM, so this works for any form whose inputs
 * are rendered through `FormField`.
 *
 * @param fieldMeta The form's `fieldMeta` keyed by field id.
 * @param doc Document to query (injectable for testing).
 * @returns The resolved target, or `null` if no invalid field is in the DOM.
 */
function resolveFirstInvalidField(
  fieldMeta: Record<string, FieldMetaLike>,
  doc: Document = document,
): FieldTarget | null {
  const firstInvalidId = getRenderedFieldIds(doc).find((fieldId) =>
    hasError(fieldMeta[fieldId]),
  );
  return resolveField(firstInvalidId, doc);
}

/**
 * Finds the first rendered field (in document order) that this form actually
 * owns and resolves its DOM elements.
 *
 * Ownership is read from `fieldMeta` (a field has an entry once its `form.Field`
 * has registered) rather than "first labelled element in the DOM". This matters
 * because a field's document position isn't always the form's: a modal renders
 * into a `<body>` portal that sits *after* the page content, so the globally
 * first labelled element can belong to a different form entirely.
 *
 * @param fieldMeta The form's `fieldMeta` keyed by field id.
 * @param doc Document to query (injectable for testing).
 * @returns The resolved target, or `null` if the form has no rendered field.
 */
function resolveFirstOwnedField(
  fieldMeta: Record<string, FieldMetaLike>,
  doc: Document = document,
): FieldTarget | null {
  const firstOwnedId = getRenderedFieldIds(doc).find(
    (fieldId) => fieldMeta[fieldId] !== undefined,
  );
  return resolveField(firstOwnedId, doc);
}

/**
 * Manages focus for a `@tanstack/react-form`. Drop in right after `useForm`:
 *
 * ```tsx
 * const form = useForm({ ... });
 * useFormFocusManager(form);
 * ```
 *
 * It does two things, both of which rely on fields being rendered through
 * `FormField` (or `Fieldset`) so they can be located via the
 * `aria-labelledby="${id}-label"` markers those components emit — always pass
 * `name={field.name}` to every `FormField`:
 *
 * 1. **Autofocus the first field.** The first time the form renders a field, the
 *    form's own first field (in document order) is focused — including when that
 *    is after the host mounts, e.g. a panel/modal gated behind
 *    `if (!isVisible) return null`. This runs exactly once — a ref guard makes
 *    every later commit (and React StrictMode's double-invoke) bail.
 * 2. **Scroll to the first error on a failed submit.** Each time a submit
 *    settles with the form invalid, the first invalid field is scrolled into
 *    view and focused.
 */
export function useFormFocusManager(
  form: AnyFormApi,
  { autoFocus = true }: FormFocusManagerOptions = {},
): void {
  // 1. Autofocus the form's first field, exactly once, the first time the form
  // actually renders a field. The ref guard makes it once-only: every later
  // commit bails.
  //
  // This runs on every commit (no deps) and only *reads* `form.store.state` — it
  // deliberately does not subscribe to the store. Subscribing (e.g. to a field
  // count) re-renders the host on every `fieldMeta` change, which deadlocks any
  // form that syncs external state back in via `setFieldValue` (render → update
  // → render). Reading is enough: child `form.Field`s register before this
  // parent effect runs (effects fire child-to-parent), so `fieldMeta` already
  // reflects them — including on the commit where a previously-closed
  // (`if (!isVisible) return null`) panel/modal first reveals its fields.
  const hasAutoFocusedRef = useRef(false);

  useEffect(() => {
    if (!autoFocus || hasAutoFocusedRef.current) {
      return;
    }

    const target = resolveFirstOwnedField(form.store.state.fieldMeta);
    if (!target?.focusTarget || !isAutofocusableControl(target.focusTarget)) {
      return;
    }

    hasAutoFocusedRef.current = true;
    // Plain focus (no scroll): the first field sits at the top of a freshly
    // shown form, already in view — mirror native `autofocus`.
    target.focusTarget.focus();
  });

  // 2. After a failed submit, scroll the first invalid field (in document
  // order) into view and focus it.
  const invalidFieldRef = useRef<HTMLElement | null>(null);
  const invalidFieldFocusRef = useRef<HTMLElement | null>(null);
  const makeFirstInvalidFieldVisible = useScrollIntoView({
    el: invalidFieldRef,
    focus: invalidFieldFocusRef,
  });

  // Each settled submit attempt bumps `submissionAttempts`; react once it
  // settles (not mid-submit) so we don't run against a stale/validating state.
  // Select primitives separately so the hook doesn't allocate a new object on
  // every store update (which would re-render consumers on each keystroke).
  const submissionAttempts = useStore(
    form.store,
    (state) => state.submissionAttempts,
  );
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);

  const handledAttemptRef = useRef(0);

  useEffect(() => {
    if (isSubmitting || submissionAttempts === handledAttemptRef.current) {
      return;
    }
    handledAttemptRef.current = submissionAttempts;

    if (form.store.state.isValid) {
      return;
    }

    const target = resolveFirstInvalidField(form.store.state.fieldMeta);
    if (!target) {
      return;
    }

    invalidFieldRef.current = target.scrollTarget;
    invalidFieldFocusRef.current = target.focusTarget;
    makeFirstInvalidFieldVisible();
  }, [submissionAttempts, isSubmitting, form, makeFirstInvalidFieldVisible]);
}
